/* -------------------------------------------------------
   ChatComposer — Auto-grow textarea with send button.
   Enter sends, Shift+Enter inserts a newline.
   ------------------------------------------------------- */

import React, { useRef, useCallback, useEffect } from 'react';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
}

const ChatComposer: React.FC<Props> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Ask about your taxes or portfolio\u2026',
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  /** Auto-resize textarea to fit content (up to 200px) */
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, []);

  useEffect(resize, [value, resize]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) onSend();
    }
  };

  return (
    <div className="cv2-composer">
      <div className="cv2-composer-inner">
        <div className="cv2-textarea-wrap">
          <textarea
            ref={ref}
            className="cv2-textarea"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            aria-label="Message input"
          />
          <button
            className="cv2-send-btn"
            onClick={onSend}
            disabled={disabled || !value.trim()}
            aria-label="Send message"
          >
            {/* send arrow icon */}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ChatComposer);
